/**
 * brand.ts — THE single source of truth for the DJ Longbeard identity.
 *
 * Every visual + link on the site renders from this file. Change the avatar, a
 * palette token, the bio, or a social handle in ONE place here and it propagates
 * across the whole site (and is the contract any future system — radio, merch,
 * content engine — reads from).
 *
 * Mirror of the owned-handle list in `../../handle-grab/build.py` (HAVE entries).
 * Brand rules: lowercase handles, display name "DJ Longbeard", mystical-folk.
 */

/* ------------------------------------------------------------------ identity */

export const identity = {
  /** display name — capitalized (the card headline) */
  name: 'DJ Longbeard',
  /** lowercase handle */
  handle: '@djlongbeard',
  /** the one bio line, rendered in quotes on the card */
  quote: 'music is magic',
  location: 'joshua tree, california',
  /** The identity icon. ONE path, referenced everywhere (card, admin, OG). */
  avatar: '/avatar.jpg',
  cover: '/cover.jpg',
  domain: 'djlongbeard.com',
  adminEmail: 'admin@djlongbeard.com',
  /** public contact inbox (the email tile in the link hub) */
  contactEmail: 'contact@djlongbeard.com',
} as const;

/* ------------------------------------------------------------------- palette */
/**
 * Monochromatic neutral tokens (mirrors the APPLIED copy in `index.css :root`).
 * Grayscale chrome only — no blue, no gold, one white + a gray ramp. The only
 * color in the app comes from the brand-logo squircle tiles.
 */
export const palette = {
  bg: '#0B0B0C',
  bgDeep: '#050506',
  surface1: '#161618',
  surface2: '#202023',
  text: '#FAFAFA',
  textMuted: '#8E8E93',
  textFaint: '#5A5A5E',
} as const;

/* --------------------------------------------------------------------- radio */
/**
 * The future 24/7 live YouTube radio stream, surfaced as a "now playing" bar:
 * always streaming (mute-only, no play/pause), showing the current track +
 * artist, a visualizer, and an external link to watch on YouTube. `nowPlaying`
 * is example data for the showcase until the real stream feeds it.
 */
export const radio = {
  name: 'DJ Longbeard Radio',
  nowPlaying: { track: 'Amen Spell', artist: 'DJ Longbeard' },
  /** external — opens the live stream / channel on YouTube */
  youtubeUrl: 'https://youtube.com/@djlongbeard/live',
} as const;

/* --------------------------------------------------------------------- links */

export interface BrandLink {
  /** simple-icons slug — resolves the official white glyph + brand hex tile */
  icon: string;
  /** proper-cased, brand-recognizable label */
  label: string;
  url: string;
  /** first-person tagline shown as a tooltip on hover */
  tagline: string;
  /** optional override tile background (e.g. Instagram's gradient) */
  tile?: string;
}

/**
 * Every owned handle, in Bobby's chosen order. The YouTube URL uses
 * `?sub_confirmation=1` so it opens the channel with YouTube's native
 * "Subscribe to DJ Longbeard?" confirmation prompt. Linktree is included too.
 */
export const links: BrandLink[] = [
  { icon: 'youtube', label: 'YouTube', url: 'https://youtube.com/@djlongbeard?sub_confirmation=1', tagline: 'Listen to my music on YouTube' },
  { icon: 'x', label: 'X', url: 'https://x.com/DJLongbeard', tagline: 'Read my words of wisdom on X' },
  {
    icon: 'instagram',
    label: 'Instagram',
    url: 'https://instagram.com/djlongbeard',
    tagline: 'See my latest posts on Instagram',
    tile: 'linear-gradient(135deg, #feda75, #fa7e1e 28%, #d62976 55%, #962fbf 78%, #4f5bd5)',
  },
  { icon: 'soundcloud', label: 'SoundCloud', url: 'https://soundcloud.com/djlongbeard', tagline: 'Stream my tracks on SoundCloud' },
  { icon: 'bandcamp', label: 'Bandcamp', url: 'https://djlongbeard.bandcamp.com', tagline: 'Collect my releases on Bandcamp' },
  { icon: 'mixcloud', label: 'Mixcloud', url: 'https://mixcloud.com/djlongbeard', tagline: 'Hear my mixes on Mixcloud' },
  { icon: 'twitch', label: 'Twitch', url: 'https://twitch.tv/djlongbeard', tagline: 'Catch me live on Twitch' },
  { icon: 'facebook', label: 'Facebook', url: 'https://facebook.com/djlongbeard', tagline: 'Follow along on Facebook' },
  { icon: 'tumblr', label: 'Tumblr', url: 'https://djlongbeard.tumblr.com', tagline: 'Wander my musings on Tumblr' },
  { icon: 'reddit', label: 'Reddit', url: 'https://reddit.com/user/djlongbeard', tagline: 'Join me on Reddit' },
  { icon: 'linktree', label: 'Linktree', url: 'https://linktr.ee/djlongbeard', tagline: 'Find every link on Linktree' },
  { icon: 'email', label: 'Email', url: 'mailto:contact@djlongbeard.com', tagline: 'Send me a message' },
];
